import { call, put, take, select } from 'redux-saga/effects';
import dayjs from 'dayjs';
import _ from 'lodash';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as types from 'src/state/ducks/order/types';
import * as actions from 'src/state/ducks/order/actions';
import * as api from 'src/state/ducks/order/api';
import * as userApi from 'src/state/ducks/user/api';
import * as userActions from 'src/state/ducks/user/actions';
import { State } from 'src/state/ducks';
import { Status } from 'src/constants/Interface';
import ValidationManager from 'src/firebase/db/ValidationManager';
import { OrderEntity, orderEntityKeys } from 'src/firebase/db/Orders';
import { toaster } from 'src/helpers/UtilityHelper';

dayjs.extend(isSameOrAfter);

function* validateOrder() {
  while (true) {
    const { payload } = yield take(types.VALIDATE_ORDER);

    // 入力された値を検証する
    const form = payload as OrderEntity;
    const validator = new ValidationManager();

    // 入力必須
    const IS_REQUIRED = ['scheduled_time', 'last_pickup_time', 'can_order_time'];

    orderEntityKeys.forEach((key) => {
      if (IS_REQUIRED.includes(key)) {
        validator.isRequired(key, form[key]);
      }

      switch (key) {
      }
    });

    if (validator.isPass()) {
      // yield put(push(`/order/create/confirm}`));
      yield put(actions.successValidateOrder(payload));
    } else {
      const errors = validator.throwValidateError(validator.getErrors());
      yield put(actions.failValidateOrder(errors));
    }
  }
}

function* getOrders() {
  while (true) {
    const { payload } = yield take(types.GET_ORDERS);

    try {
      const res = yield call(api.getOrders, payload.shopId);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetOrders(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetOrders(res));
      }
    } catch (err) {
      yield put(
        actions.failGetOrders({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getDayOrders() {
  while (true) {
    const { payload } = yield take(types.GET_DAY_ORDERS);
    try {
      // 日付別の注文データを取得
      const res = yield call(
        api.getDayOrders,
        payload.shopId,
        payload.startDate,
        payload.endDate,
        payload.target,
        payload.filter,
        payload.noDetails,
      );
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetDayOrders(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetDayOrders(res));
      }

      if (!payload.noUsers) {
        // 当日の注文データからユーザー別の注文履歴も取得する
        const state: State = yield select();
        const orders = state.order.filterByDate.resource;
        const user_ids = _.uniq(orders.map(({ user_id }) => user_id));
        const usersOrdersRes = yield call(api.getUsersOrders, user_ids);
        if (usersOrdersRes.status === Status.SUCCESS) {
          yield put(actions.successGetUsersOrders(usersOrdersRes));
        } else if (usersOrdersRes.status === Status.INVALID) {
          yield put(actions.failGetUsersOrders(usersOrdersRes));
        }

        // ユーザー情報を取得
        const usersRes = yield call(userApi.getUsers, user_ids);
        if (usersRes.status === Status.SUCCESS) {
          yield put(userActions.successGetUsers(usersRes));
        } else if (usersRes.status === Status.INVALID) {
          yield put(userActions.failGetUsers(usersRes));
        }
      }
    } catch (err) {
      console.log(err);
      yield put(
        actions.failGetDayOrders({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
      yield put(
        actions.failGetUsersOrders({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* getUsersOrders() {
  while (true) {
    const { payload } = yield take(types.GET_USERS_ORDERS);
    try {
      const res = yield call(api.getUsersOrders, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetUsersOrders(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetUsersOrders(res));
      }
    } catch (err) {
      yield put(
        actions.failGetUsersOrders({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* createOrder() {
  while (true) {
    const { payload } = yield take(types.CREATE_ORDER);
    try {
      const res = yield call(api.createOrder, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successCreateOrder(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failCreateOrder(res));
      }
    } catch (err) {
      console.log({ err });
      yield put(
        actions.failCreateOrder({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* updateOrderIsConfirmed() {
  while (true) {
    const { payload } = yield take(types.UPDATE_ORDER_IS_CONFIRMED);
    try {
      const res = yield call(api.updateOrderIsConfirmed, payload.id, payload.isConfirmed);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successUpdateOrderIsConfirmed(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failUpdateOrderIsConfirmed(res));
        toaster('error', '更新に失敗しました。');
      }
    } catch (err) {
      console.log({ err });
      toaster('error', '更新に失敗しました。');
      yield put(
        actions.failUpdateOrderIsConfirmed({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

function* updateOrderIsCompleted() {
  while (true) {
    const { payload } = yield take(types.UPDATE_ORDER_IS_COMPLETED);
    try {
      const res = yield call(api.updateOrderIsCompleted, payload.id, payload.isCompleted);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successUpdateOrderIsCompleted(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failUpdateOrderIsCompleted(res));
        toaster('error', '更新に失敗しました。');
      }
    } catch (err) {
      console.log({ err });
      toaster('error', '更新に失敗しました。');
      yield put(
        actions.failUpdateOrderIsCompleted({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        }),
      );
    }
  }
}

const order = [
  getOrders(),
  getDayOrders(),
  getUsersOrders(),
  validateOrder(),
  createOrder(),
  updateOrderIsConfirmed(),
  updateOrderIsCompleted(),
];

export default order;
